import React from 'react';
import { Col, Container, Row } from 'reactstrap';
import { TbAlertTriangle } from 'react-icons/tb';

// A lazy-loaded chunk can fail to load when a new build has been deployed and the
// old hashed chunk files no longer exist on the server (the SPA fallback then
// returns index.html, which the browser refuses to execute as JS). In that case
// a single hard reload fetches the fresh index.html + current chunks.
const isChunkLoadError = error => {
  if (!error) return false;
  const message = error.message || '';
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /is not executable, and strict MIME type checking is enabled/i.test(message)
  );
};

const CHUNK_RELOAD_KEY = 'chunk-reload-at';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error)) {
      // Guard against a reload loop: only auto-reload once per minute.
      const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
      if (Date.now() - last > 60 * 1000) {
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return;
      }
    }
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="auth-page-content">
          <div className="auth-page-wrapper py-5 d-flex justify-content-center align-items-center min-vh-100">
            <div className="auth-page-content overflow-hidden p-0">
              <Container>
                <Row className="justify-content-center">
                  <Col xl={7} lg={8}>
                    <div className="text-center">
                      <TbAlertTriangle size={180} style={{ color: '#405189' }} />
                      <div className="mt-3">
                        <h3 className="text-uppercase">Something went wrong</h3>
                        <p className="text-muted mb-4">An unexpected error occurred. Please try refreshing the page.</p>
                        {process.env.NODE_ENV !== 'production' && this.state.error && (
                          <pre className="text-start bg-danger bg-opacity-10 text-danger rounded p-3 mb-4 overflow-auto small">
                            {this.state.error.toString()}
                          </pre>
                        )}
                        <div className="d-flex gap-2 justify-content-center">
                          <button className="btn btn-light" onClick={() => window.history.back()}>
                            <i className="mdi mdi-arrow-left me-1"></i>Go Back
                          </button>
                          <button className="btn btn-success" onClick={() => window.location.reload()}>
                            <i className="mdi mdi-refresh me-1"></i>Reload Page
                          </button>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Container>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
